
import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { User } from './types';

function supabaseUserToAppUser(dataUser: any): User {
  // Fills required fields for compatibility with our app's User interface
  return {
    id: dataUser.id,
    username: dataUser.user_metadata?.username || dataUser.email || "user",
    email: dataUser.email || "",
    points: 0,
    photoUrl: "",
    streakDays: 0,
    followers: [],
    following: [],
  };
}

export function useSupabaseAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen to session changes and reflect in React state
  useEffect(() => {
    const getSessionUser = async () => {
      setLoading(true);
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        // Load extended profile from 'users' table
        const { data: profiles } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.user.id)
          .single();
        if (profiles) {
          console.log('Profile found for user:', profiles);
          setUser(profiles);
        } else {
          console.log('No profile found, falling back to Supabase user:', data.user);
          setUser(supabaseUserToAppUser(data.user));
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    };
    getSessionUser();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('onAuthStateChange event:', _event, session);
      if (session?.user) {
        getSessionUser();
      } else {
        setUser(null);
      }
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  // DEBUG: Watch user/loading state changes
  useEffect(() => {
    console.log('SupabaseAuth: loading=', loading, 'user=', user);
  }, [user, loading]);

  // Auth actions
  const signup = async (username: string, email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username } }
    });
    if (error) throw error;
    // Insert into users table
    await supabase.from('users').insert({
      id: data.user.id,
      username,
      email,
      points: 0,
      photoUrl: '',
      streakDays: 0,
      followers: [],
      following: []
    });
    // reload user state
    const { data: profiles } = await supabase
      .from('users')
      .select('*')
      .eq('id', data.user.id)
      .single();
    setUser(profiles || supabaseUserToAppUser(data.user));
    return data.user;
  };

  const login = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    // reload user state
    const { data: profiles } = await supabase
      .from('users')
      .select('*')
      .eq('id', data.user.id)
      .single();
    setUser(profiles || supabaseUserToAppUser(data.user));
    return data.user;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  // --- Add missing methods for compatibility ---

  // isAuthenticated: shortcut property
  const isAuthenticated = !!user;

  // updateUserPhoto: update photoUrl in DB and state
  const updateUserPhoto = async (photoUrl: string) => {
    if (!user) return;
    await supabase.from('users').update({ photoUrl }).eq('id', user.id);
    setUser({ ...user, photoUrl });
  };

  // addPoints: add N points to user
  const addPoints = async (points: number) => {
    if (!user) return;
    const newPoints = (user.points ?? 0) + points;
    await supabase.from('users').update({ points: newPoints }).eq('id', user.id);
    setUser({ ...user, points: newPoints });
  };

  // Following helpers
  const toggleFollow = async (targetUserId: string) => {
    if (!user || user.id === targetUserId) return;
    // fetch fresh
    const { data: targetUserData } = await supabase
      .from('users')
      .select('followers')
      .eq('id', targetUserId)
      .maybeSingle();
    let isNowFollowing = false;
    let following = Array.isArray(user.following) ? [...user.following] : [];
    if (following.includes(targetUserId)) {
      // Unfollow
      following = following.filter(id => id !== targetUserId);
    } else {
      following.push(targetUserId);
      isNowFollowing = true;
    }
    // Update current user
    await supabase.from('users').update({ following }).eq('id', user.id);
    setUser({ ...user, following });
    // Update target's followers
    let followers = Array.isArray(targetUserData?.followers) ? [...targetUserData!.followers] : [];
    if (isNowFollowing) {
      followers.push(user.id);
    } else {
      followers = followers.filter(id => id !== user.id);
    }
    await supabase.from('users').update({ followers }).eq('id', targetUserId);
  };

  // isFollowing: does the user follow targetUserId
  const isFollowing = (targetUserId: string) => {
    if (!user || !Array.isArray(user.following)) return false;
    return user.following.includes(targetUserId);
  };

  return {
    user,
    loading,
    signup,
    login,
    logout,
    isAuthenticated,
    updateUserPhoto,
    addPoints,
    toggleFollow,
    isFollowing,
  };
}
